import { fetchPlatformOperationLogs, type PlatformOperationLogDto } from "../../../api/platformOperationLogs";

export interface LogEntry {
  id: string;
  timestamp: string;
  operator: string;
  operatorRole: string;
  actionType: "view" | "suspend" | "activate" | "plan_change" | "billing" | "login" | "logout" | "create_tenant" | "edit_settings" | "impersonate";
  targetTenant: string;
  targetTenantCode: string;
  detail: string;
  ip: string;
  result: "success" | "failed" | "warning";
}

type Listener = () => void;

const formatNow = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const actionWhitelist = new Set<LogEntry["actionType"]>([
  "view",
  "suspend",
  "activate",
  "plan_change",
  "billing",
  "login",
  "logout",
  "create_tenant",
  "edit_settings",
  "impersonate",
]);

function mapApiDtoToLogEntry(r: PlatformOperationLogDto): LogEntry {
  const at = actionWhitelist.has(r.actionType as LogEntry["actionType"])
    ? (r.actionType as LogEntry["actionType"])
    : "view";
  const res =
    r.result === "warning" || r.result === "failed" ? (r.result as LogEntry["result"]) : "success";
  return {
    id: r.id,
    timestamp: r.timestamp,
    operator: r.operator,
    operatorRole: r.operatorRole,
    actionType: at,
    targetTenant: r.targetTenant,
    targetTenantCode: r.targetTenantCode,
    detail: r.detail,
    ip: r.ip,
    result: res,
  };
}

const store: {
  logs: LogEntry[];
  listeners: Listener[];
} = {
  logs: [],
  listeners: [],
};

export const logStore = {
  addLog(entry: Omit<LogEntry, "id" | "timestamp">) {
    const newEntry: LogEntry = {
      ...entry,
      id: `l${Date.now()}`,
      timestamp: formatNow(),
    };
    store.logs.unshift(newEntry);
    store.listeners.forEach((fn) => fn());
  },

  replaceFromApi(entries: LogEntry[]): void {
    store.logs.length = 0;
    store.logs.push(...entries);
    store.listeners.forEach((fn) => fn());
  },

  async refreshFromApi(): Promise<void> {
    try {
      const rows = await fetchPlatformOperationLogs(400);
      logStore.replaceFromApi(rows.map(mapApiDtoToLogEntry));
    } catch {
      /* 保留现有列表 */
    }
  },

  getLogs(): LogEntry[] {
    return [...store.logs];
  },

  subscribe(fn: Listener): () => void {
    store.listeners.push(fn);
    return () => {
      const idx = store.listeners.indexOf(fn);
      if (idx > -1) store.listeners.splice(idx, 1);
    };
  },

  // Helper: log a tenant view action
  logView(tenantName: string, tenantCode: string) {
    logStore.addLog({
      operator: "平台管理员",
      operatorRole: "超级管理员",
      actionType: "view",
      targetTenant: tenantName,
      targetTenantCode: tenantCode,
      detail: `打开租户「${tenantName}」详情面板，查看基本信息`,
      ip: "192.168.1.101",
      result: "success",
    });
  },

  logSuspend(tenantName: string, tenantCode: string) {
    logStore.addLog({
      operator: "平台管理员",
      operatorRole: "超级管理员",
      actionType: "suspend",
      targetTenant: tenantName,
      targetTenantCode: tenantCode,
      detail: `执行停用操作：租户「${tenantName}」已被停用，所有用户将无法登录后台`,
      ip: "192.168.1.101",
      result: "warning",
    });
  },

  logActivate(tenantName: string, tenantCode: string) {
    logStore.addLog({
      operator: "平台管理员",
      operatorRole: "超级管理员",
      actionType: "activate",
      targetTenant: tenantName,
      targetTenantCode: tenantCode,
      detail: `执行激活操作：租户「${tenantName}」已重新激活，恢复运营中状态`,
      ip: "192.168.1.101",
      result: "success",
    });
  },

  logCreate(tenantName: string, tenantCode: string, plan: string) {
    logStore.addLog({
      operator: "平台管理员",
      operatorRole: "超级管理员",
      actionType: "create_tenant",
      targetTenant: tenantName,
      targetTenantCode: tenantCode,
      detail: `开通新租户「${tenantName}」，套餐：${plan}，代码：${tenantCode}，状态：待审核`,
      ip: "192.168.1.101",
      result: "success",
    });
  },

  logImpersonate(tenantName: string, tenantCode: string) {
    logStore.addLog({
      operator: "平台管理员",
      operatorRole: "超级管理员",
      actionType: "impersonate",
      targetTenant: tenantName,
      targetTenantCode: tenantCode,
      detail: `以平台管理员身份进入租户「${tenantName}」后台查看，已开启模拟身份会话`,
      ip: "192.168.1.101",
      result: "success",
    });
  },
};
